/**
 * Client-seitiges Parsen/Normalisieren der Spielerliste fürs Scoreboard.
 * Quelle ist entweder der rohe `bf2cc pl`-Dump (REST players_list) oder die
 * bereits geparsten Player-Objekte aus dem WS-Push (Rust parse::players).
 */

export interface ScorePlayer {
  id: number;
  name: string;
  pid: string;
  ip: string;
  rank: string;
  team: 0 | 1 | 2; // 1=PAC, 2=EU
  score: number;
  kills: number;
  deaths: number;
  ping: string;
  bot: boolean;
  alive: boolean;
  position: WorldPosition | null;
  squad: string;
  squadLeader: boolean;
  commander: boolean;
  kit: string;
  vehicle: string;
}

export interface WorldPosition {
  x: number;
  y?: number;
  z: number;
}

export interface PlayerTacTelemetry {
  id: number;
  name: string;
  team: 0 | 1 | 2;
  pid: string;
  squad: string;
  squadLeader: boolean;
  commander: boolean;
  kit: string;
  vehicle: string;
  world: { x: number; z: number };
}

const num = (v: unknown): number => {
  const n = parseInt(String(v ?? ""), 10);
  return Number.isFinite(n) ? n : 0;
};
const truthy = (v: unknown): boolean => v === "1" || v === 1 || v === true || String(v).toLowerCase() === "true";

export function parseWorldPosition(value: unknown): WorldPosition | null {
  let x: number;
  let y: number | undefined;
  let z: number;

  if (typeof value === "string") {
    const parts = value.split(",").map((p) => Number(p.trim()));
    if (parts.length < 3) return null;
    [x, y, z] = [parts[0]!, parts[1], parts[2]!];
  } else if (value && typeof value === "object") {
    const p = value as Record<string, unknown>;
    x = Number(p.x);
    y = p.y === undefined ? undefined : Number(p.y);
    z = Number(p.z);
  } else {
    return null;
  }

  if (!Number.isFinite(x) || !Number.isFinite(z)) return null;
  if (y !== undefined && !Number.isFinite(y)) y = undefined;
  if (x === 0 && z === 0) return null;
  return y === undefined ? { x, z } : { x, y, z };
}

/** Parst rohen `bf2cc pl`-Dump (Tab-getrennt) in Scoreboard-Zeilen. */
export function parseBf2ccPl(raw: string): ScorePlayer[] {
  const out: ScorePlayer[] = [];
  for (const line of String(raw).split(/\r?\n/)) {
    if (!line.includes("\t")) continue;
    const f = line.split("\t");
    if (f.length < 11) continue;
    const t = num(f[2]);
    out.push({
      id: num(f[0]),
      name: f[1] ?? "",
      pid: f[10] ?? "",
      ip: f[18] ?? "",
      rank: f[39] ?? "",
      team: t === 1 ? 1 : t === 2 ? 2 : 0,
      ping: f[3] ?? "",
      bot: truthy(f[7]),
      alive: truthy(f[8]),
      kills: num(f[31]),
      squad: f[14] ?? "",
      squadLeader: truthy(f[15]),
      commander: truthy(f[16]),
      kit: f[34] ?? "",
      deaths: num(f[36]),
      score: num(f[37]),
      vehicle: f[38] ?? "",
      position: parseWorldPosition(f[40] ?? ""),
    });
  }
  return out;
}

/** Normalisiert die geparsten WS-Player-Objekte auf dieselbe Form. */
export function fromLivePlayers(players: Record<string, unknown>[]): ScorePlayer[] {
  return players.map((p) => {
    const team = p.team === "pac" ? 1 : p.team === "eu" ? 2 : 0;
    return {
      id: num(p.id),
      name: String(p.name ?? ""),
      pid: String(p.pid ?? ""),
      ip: String(p.ip ?? ""),
      rank: String(p.rank ?? ""),
      team: team as 0 | 1 | 2,
      ping: String(p.ping ?? ""),
      bot: truthy(p.bot),
      alive: truthy(p.alive),
      kills: num(p.kills),
      squad: String(p.squad ?? ""),
      squadLeader: truthy(p.squadLeader ?? p.squad_leader),
      commander: truthy(p.commander),
      kit: String(p.kit ?? ""),
      deaths: num(p.deaths),
      score: num(p.score),
      vehicle: String(p.vehicle ?? ""),
      position: parseWorldPosition(p.position),
    };
  });
}

function playerKey(p: ScorePlayer): string | null {
  if (p.pid) return `pid:${p.pid}`;
  if (p.id) return `id:${p.id}`;
  if (p.name) return `name:${p.name.toLowerCase()}`;
  return null;
}

export function mergeScorePlayers(existing: ScorePlayer[], live: ScorePlayer[]): ScorePlayer[] {
  const byKey = new Map<string, ScorePlayer>();
  for (const p of existing) {
    const key = playerKey(p);
    if (key && !byKey.has(key)) byKey.set(key, p);
  }

  return live.map((p) => {
    const key = playerKey(p);
    const previous = key ? byKey.get(key) : undefined;
    if (!previous) return p;
    return {
      ...p,
      ip: p.ip || previous.ip,
      rank: p.rank || previous.rank,
    };
  });
}

export function playerTacTelemetry(players: ScorePlayer[]): PlayerTacTelemetry[] {
  return players
    .filter((p) => p.position !== null)
    .map((p) => ({
      id: p.id,
      name: p.name,
      team: p.team,
      pid: p.pid,
      squad: p.squad,
      squadLeader: p.squadLeader,
      commander: p.commander,
      kit: p.kit,
      vehicle: p.vehicle,
      world: { x: p.position!.x, z: p.position!.z },
    }));
}

export function rankIconPath(rank: string): string | null {
  const value = String(rank).trim();
  if (!/^\d+$/.test(value)) return null;
  const n = Number(value);
  if (!Number.isInteger(n) || n < 0 || n > 43) return null;
  return `/assets/ranks/rank_${n}.png`;
}

const MODES: Record<string, string> = {
  gpm_ti: "Titan",
  gpm_cq: "Conquest",
  gpm_nv: "Assault Lines",
  gpm_sl: "Sector Control",
  gpm_coop: "Co-op",
  gpm_sp: "Singleplayer",
};

export const modeLabel = (mode: string): string => MODES[mode] ?? (mode || "—");

export function formatMapName(value: string): string {
  const raw = String(value ?? "").trim();
  if (!raw) return "UNKNOWN MAP";
  return raw
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .toLowerCase()
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

export function formatMapMode(map: string | undefined | null, mode: string | undefined | null): string {
  const mapRaw = String(map ?? "").trim();
  const modeRaw = String(mode ?? "").trim();
  const parts: string[] = [];
  if (mapRaw) parts.push(formatMapName(mapRaw));
  if (modeRaw) parts.push(modeLabel(modeRaw));
  return parts.length ? parts.join(" · ") : "—";
}

export interface TitanStatus {
  shieldPct: number;
  hullPct: number;
  text: string;
}

export interface BattleResource {
  label: string;
  sub: string;
  pac: string;
  eu: string;
  titan: boolean;
}

export const clamp = (value: number, min = 0, max = 100): number => Math.min(max, Math.max(min, value));

export function titanStatus(value: number): TitanStatus {
  const v = clamp(Number.isFinite(value) ? value : 0);
  if (v === 0) return { shieldPct: 0, hullPct: 0, text: "Titan zerstört" };
  if (v > 60) {
    const shieldPct = Math.round(((v - 60) / 40) * 100);
    return { shieldPct, hullPct: 100, text: `Schild ${shieldPct}%` };
  }
  const hullPct = Math.round((v / 60) * 100);
  return { shieldPct: 0, hullPct, text: `Schild down · Hülle ${hullPct}%` };
}

export function formatBattleResource(mode: string, pac: number, eu: number): BattleResource {
  if (mode === "gpm_ti") {
    return {
      label: "Titan-Status",
      sub: "PAC / EU",
      pac: titanStatus(pac).text,
      eu: titanStatus(eu).text,
      titan: true,
    };
  }
  return {
    label: "Tickets",
    sub: "PAC : EU",
    pac: String(pac),
    eu: String(eu),
    titan: false,
  };
}
